import React, { useState, useEffect } from "react"
import { Card, Col, Row } from "react-bootstrap"
import { Button, Box } from "@material-ui/core"
import Chart from "react-apexcharts"
import FMCSAsearch from "components/Applicants/partial/Modals/FMCSAsearch"
import {
  Status,
  currency
} from 'constants/chartData'
import { currencyFormatter } from 'tools/helper'
import { Toast } from 'components/shared/Toaster'
import strings from "strings"
import "./Dashboard.scss"
import AuthService from "tools/AuthService"
import { getDashboardFilters, setDashboardFilters } from "../../actions/user"
import { getCreditCosts } from "../../actions/applicants"
import DemoModeSwitch from "../DemoMode/DemoModeSwitch"
import { useAppContext } from "../../context/AppContext"
import NoAvailableData from "./components/NoAvailableData"
import DsButton from "components/ds/Button"
import PageHeader from "components/ds/PageHeader"
import DsIcon from "components/ds/Icon"


const t = strings.DASHBOARD

const MONITORING_ROWS = 5

/**
 * @param {Array} data Application overview data 
 * @returns Stateless Component
 */
const Overview = (props) => {
  const {
    data
  } = props

  return (
    <div className="overViewCardContainer">
      {
        data.map((item, index) => {
          const value = currency.includes(item.name) && typeof item.value === "number"
            ? `$${currencyFormatter(item.value)}`
            : item.value || 0

          const hasDelta = item.delta != null && item.delta !== ''
          const direction = item.direction || (Number(item.delta) < 0 ? 'down' : 'up')

          return (
            <div key={index} className="kpiTile">
              <div className="kpiTile__head">
                <span className="kpiTile__label">{item.label || item.name}</span>
                {item.dsIcon && <DsIcon name={item.dsIcon} size={20} className="kpiTile__icon" />}
              </div>
              <span className="kpiTile__value">{value}</span>
              {hasDelta
                ? (
                  <span className="kpiTile__delta">
                    <span className={`kpiTile__deltaValue kpiTile__deltaValue--${direction}`}>
                      {direction === 'down' ? '\u2193' : '\u2191'} {item.delta}
                    </span>
                    <span className="kpiTile__baseline">{item.baseline || t.VS_LAST_MONTH}</span>
                  </span>
                )
                : <span className="kpiTile__baseline">{item.emptyNote || ''}</span>
              }
            </div>
          )
        })
      }
    </div>
  )
}


const Dashboard = (props) => {
  const {
    /* actions dispatching from the container 
     to handle dashboard and fmcsa API */
    getFmcsaData,
    getDashboardOverview,
    getDashboardApplication,
    getDashboardPortfolio,
    getMonitoringAlerts,
    /* dashboard charts, overview and fmcsa data 
    fetched from redux state and 
    passed through container as prop */
    dispatchProcessing,
    fmcsaData,
    overview,
    applicationGrowth,
    portFolioGrowth,
    fmcsaParam,
    history,
    hideFmcsa,
    monitoringAlertsList = [],
  } = props

  const [showModal, setModal] = useState(false)
  const isPlatformAdmin = AuthService.hasRole(strings.ROX_ADMIN)
  const [hiddenDonutSeries, setHiddenDonutSeries] = useState([]);
  const [hiddenBarLegends, setHiddenBarLegends] = useState([]);
  const [originalDonutSeries, setOriginalDonutSeries] = useState([]);
  const [originalBarLegends, setOriginalBarLegends] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const { isDemoMode, setDemoMode,setHasClientPortal } = useAppContext()
  const { monitoringEnabled, creditUsageEnabled } = AuthService.getClientFeatures()
  const [creditCosts, setCreditCosts] = useState([])

  if (isDemoMode === null) {
    setDemoMode(props.user.demoMode)
  }

  setHasClientPortal(props?.user?.hasClientPortal)

  useEffect(() => {
    dispatchProcessing(true)
    getDashboardOverview(isPlatformAdmin, isDemoMode).then(res => {
      getDashboardApplication(isPlatformAdmin, isDemoMode).then(res => {
        getDashboardPortfolio(isPlatformAdmin, isDemoMode).finally(() => {
          dispatchProcessing(false)
        })
      })
    })
    if (monitoringEnabled) {
      getMonitoringAlerts();
    }
  }, [
    getDashboardOverview,
    getDashboardApplication,
    getDashboardPortfolio,
    dispatchProcessing,
    isPlatformAdmin,
    isDemoMode,
    monitoringEnabled,
    getMonitoringAlerts
  ])

  useEffect(() => {
    if (creditUsageEnabled) {
      getCreditCosts()()
        .then((res) => {
          setCreditCosts(res || [])
        })
        .catch((err) => {
          console.error('Error fetching credit costs:', err)
          setCreditCosts([])
        })
    }
  }, [creditUsageEnabled])

  useEffect(() => {
    dispatchProcessing(true);
    if (originalDonutSeries && originalBarLegends) {
      const donutChanged = JSON.stringify(originalDonutSeries) !== JSON.stringify(hiddenDonutSeries);
      const barChanged = JSON.stringify(originalBarLegends) !== JSON.stringify(hiddenBarLegends);
      setHasChanges(donutChanged || barChanged);
    }
    dispatchProcessing(false);
    // eslint-disable-next-line
  }, [hiddenDonutSeries, hiddenBarLegends, originalDonutSeries, originalBarLegends]);

  useEffect(() => {
  const getFilters = async () => {
    try {
      dispatchProcessing(true);
      const response = await getDashboardFilters();
      if (response && response.hiddenDonutSeries && response.hiddenBarLegends) {
        setHiddenDonutSeries(response.hiddenDonutSeries);
        setHiddenBarLegends(response.hiddenBarLegends);
        setOriginalDonutSeries(response.hiddenDonutSeries);
        setOriginalBarLegends(response.hiddenBarLegends);
      }
      setHasChanges(false);
      dispatchProcessing(false);
      return response;
    } catch (error) {
      dispatchProcessing(false);
    }
  };
    getFilters()
  }, [dispatchProcessing])

  const toggleSeries = (status) => {
    setHiddenDonutSeries((prevHidden) =>
      prevHidden?.includes(status)
        ? prevHidden.filter((s) => s !== status)
        : [...prevHidden, status]
    );
  };

  const filterSeries = (data) => {
    if (!data || !Array.isArray(data)) return [];

    // Ensure the array has the same length as Status array and fill missing elements
    const normalizedData = [];
    for (let i = 0; i < Status.length; i++) {
      const value = data[i];
      if (value === undefined || value === null || value === '') {
        normalizedData[i] = 0;
      } else {
        normalizedData[i] = Number(value);
      }
    }

    // Apply hidden series filter
    return normalizedData.map((value, index) => {
      const isHidden = hiddenDonutSeries?.includes(Status[index]?.status);
      return isHidden ? 0 : value;
    });
  };

  const applicationDonut = {
    options: {
      chart: {
        type: 'donut',
        height: 350
      },
      labels: Status.map(s => s.status),
      colors: Status.map(s => s.chartColor),
      legend: {
        show: false
      },
      plotOptions: {
        pie: {
          donut: {
            size: '65%',
            labels: {
              show: true,
              total: {
                show: true,
                showAlways: true,
                fontSize: '22px',
                fontWeight: 600,
                color: '#373d3f',
                formatter: function (w) {
                  return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                }
              }
            }
          }
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        width: 2,
        colors: ['#fff']
      }
    },
    series: filterSeries(applicationGrowth?.openApplications || []),
  };

  const applicationValueDonut = {
    options: {
      chart: {
        type: 'donut',
        height: 350
      },
      labels: Status.map(s => s.status),
      colors: Status.map(s => s.chartColor),
      legend: {
        show: false
      },
      plotOptions: {
        pie: {
          donut: {
            size: '65%',
            labels: {
              show: true,
              total: {
                show: true,
                showAlways: true,
                fontSize: '22px',
                fontWeight: 600,
                color: '#373d3f',
                formatter: function (w) {
                  const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                  return `$${total.toLocaleString()}`;
                }
              }
            }
          }
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        width: 2,
        colors: ['#fff']
      }
    },
    series: filterSeries(applicationGrowth?.applicationsValue || []),
  };

  const BarChart = (months, setHiddenBarLegends) => {
    return {
      options: {
        chart: {
          type: 'bar',
          height: '10rem',
          events: {
            legendClick: (chartContext, seriesIndex, config) => {
              const clickedSeries = config.config.series[seriesIndex].name;
              setHiddenBarLegends((prev) =>
                prev?.includes(clickedSeries)
                  ? prev.filter(item => item !== clickedSeries)
                  : [...prev, clickedSeries]
              );
            }
          }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            endingShape: 'rounded'
          },
        },
        dataLabels: {
          enabled: true
        },
        stroke: {
          show: true,
          width: 2,
          colors: ['transparent']
        },
        xaxis: {
          categories: months || ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        },
        yaxis: {
          title: {
            text: ''
          }
        },
        fill: {
          opacity: 1
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val
            }
          }
        }
      }
    };
  };

  const portfolioOptions = {
    ...BarChart(portFolioGrowth?.months, setHiddenBarLegends),
    series: [
      {
        name: strings.DASHBOARD.NEW_APPLICATIONS,
        data: hiddenBarLegends?.includes(strings.DASHBOARD.NEW_APPLICATIONS)
          ? Array(portFolioGrowth?.new?.length || 0).fill(0)
          : portFolioGrowth?.new || [],
      },
      {
        name: strings.DASHBOARD.COMPLETED_APPLICATIONS,
        data: hiddenBarLegends?.includes(strings.DASHBOARD.COMPLETED_APPLICATIONS)
          ? Array(portFolioGrowth?.completed?.length || 0).fill(0)
          : portFolioGrowth?.completed || [],
      },
    ],
    options: {
      ...BarChart(portFolioGrowth?.months, setHiddenBarLegends).options,
      legend: {
        show: false
      }
    }
  };


  const BarChartLegend = () => {
    const barChartColors = ["#008FFB", "#00E396"];
    const barChartSeries = [strings.DASHBOARD.NEW_APPLICATIONS, strings.DASHBOARD.COMPLETED_APPLICATIONS];

    return (
      <div className="colorCodeContainer">
        {barChartSeries.map((item, index) => (
          <div
            key={item}
            onClick={() => {
              setHiddenBarLegends((prev) =>
                prev?.includes(item)
                  ? prev.filter(legend => legend !== item)
                  : [...prev, item]
              );
            }}
            style={{
              cursor: "pointer",
              opacity: hiddenBarLegends?.includes(item) ? 0.5 : 1,
              marginRight: "10px",
              display: "inline-flex",
              alignItems: "center"
            }}
          >
            <div
              style={{
                backgroundColor: barChartColors[index],
                width: "12px",
                height: "12px",
                borderRadius: "2px",
                marginRight: "5px"
              }}
            ></div>
            <span className="legendLabel">{item}</span>
          </div>
        ))}
      </div>
    );
  };


  const DonutChartLegend = () => {
    const counts = applicationGrowth?.openApplications || []
    const rows = Status
      .map((item, index) => ({ ...item, count: Number(counts[index]) || 0 }))
      .filter(item => item.count > 0)

    if (!rows.length) return null

    return (
      <ul className='donutLegend'>
        {rows.map(item => {
          const hidden = hiddenDonutSeries?.includes(item.status)
          return (
            <li key={item.status}>
              <button
                type='button'
                className={`donutLegend__row${hidden ? ' donutLegend__row--off' : ''}`}
                onClick={() => toggleSeries(item.status)}
                aria-pressed={!hidden}
              >
                <span className='donutLegend__dot' style={{ backgroundColor: item.chartColor }} />
                <span className='donutLegend__label'>{item.status}</span>
                <span className='donutLegend__count'>{item.count}</span>
              </button>
            </li>
          )
        })}
      </ul>
    )
  }

  const handleCreateApplicationManually = () => {
    history.push(`/create-applicant`)
  }

  const handleSaveDashboardFilters = async () => {
    try {
      dispatchProcessing(true);
      const response = await setDashboardFilters({
        hiddenBarLegends: hiddenBarLegends,
        hiddenDonutSeries: hiddenDonutSeries
      });

      setOriginalDonutSeries(hiddenDonutSeries);
      setOriginalBarLegends(hiddenBarLegends);
      setHasChanges(false);

      dispatchProcessing(false);
      Toast({
        type: "success", message: t.DASHBOARD_FILTERS_SAVED_SUCCESSFULLY
      })
      return response;
    } catch (error) {
      dispatchProcessing(false);
      console.error("Error saving dashboard filters:", error);
    }
  };
  
    const resetFilters = async () => {
      try {
        dispatchProcessing(true);
        const response = await setDashboardFilters({
          hiddenBarLegends: [],
          hiddenDonutSeries: []
        });
  
        setOriginalDonutSeries([]);
        setOriginalBarLegends([]);
        setHiddenDonutSeries([]);
        setHiddenBarLegends([]);
        setHasChanges(false);
  
        dispatchProcessing(false);
        Toast({
          type: "success", message: t.DASHBOARD_FILTERS_CLEARED_SUCCESSFULLY
        })
        return response;
      } catch (error) {
        dispatchProcessing(false);
      }
    }

  return (
    <div className="dashboard">
      <PageHeader
        title={t.DASHBOARD}
        lede={t.DASHBOARD_LEDE}
        actions={
          <>
            <DemoModeSwitch/>
            {(hiddenBarLegends?.length > 0 || hiddenDonutSeries?.length > 0) &&
            (JSON.stringify(hiddenBarLegends) === JSON.stringify(originalBarLegends) &&
            JSON.stringify(hiddenDonutSeries) === JSON.stringify(originalDonutSeries))?
              (<Button
                variant='outlined'
                color="secondary"
                onClick={resetFilters}>
                  {t.CLEAR_SAVED_FILTERS}
                </Button>)
              :(<Button
                variant='outlined'
                color="secondary"
                className='saveFiltersBtn'
                onClick={handleSaveDashboardFilters}
                disabled={!hasChanges}
              >
                {t.SAVE_DASHBOARD_FILTERS}
              </Button>)}
            {!isPlatformAdmin && !AuthService.isReadOnlyUser() &&
              <Button
                variant='contained'
                color='primary'
                onClick={() => {
                  if (hideFmcsa || isDemoMode) {
                    handleCreateApplicationManually()
                  } else {
                    setModal(true)
                  }
                }}
                startIcon={<DsIcon name='grid' size={18} />}
              >
                {t.CREATE_APPLICATION}
              </Button>
            }
          </>
        }
      />

      <Overview data={overview} />

      <Row className="mt-4">
        <Col className="mb-3 pl-3 pr-0" xl={6}>
          <Card>
            <Card.Body className='chartHeight'>
              {
                monitoringEnabled ? (
                  <div className='cardTitleRow'>
                    <div>
                      <span className='cardEyebrow'>{t.ALL_SOURCES}</span>
                      <Card.Title className='cardTitle'>{strings.MONITORINGS.MONITORING_UPDATES}</Card.Title>
                    </div>
                    {monitoringAlertsList?.length > MONITORING_ROWS &&
                      <DsButton
                        variant='ghost'
                        size='small'
                        onClick={() => history.push('/monitoring')}
                      >
                        {t.VIEW_ALL}
                      </DsButton>
                    }
                  </div>
                ) : (
                  <Card.Title className='cardTitle'>{isPlatformAdmin ? t.ROX_WRITE_GROWTH : t.PORTFOLIO_GROWTH}</Card.Title>
                )
              }
              {
                monitoringEnabled ? <Box className='monitoringUpdates'>
                                      {
                        monitoringAlertsList && monitoringAlertsList?.length > 0 ? monitoringAlertsList.slice(0, MONITORING_ROWS).map((item, index) => {
                            return (
                                <button
                                    type='button'
                                    key={item.uuid || index}
                                    className='monitoringRow'
                                    onClick={() => item.applicationId && history.push(`/applicant/${item.applicationId}`)}
                                >
                                    <span className='monitoringRow__badge'>
                                        <DsIcon name='alert-triangle' size={16} />
                                    </span>
                                    <span className='monitoringRow__body'>
                                        <span className='monitoringRow__name'>{item.description}</span>
                                        <span className='monitoringRow__meta'>{item.businessName} · {item.createdAt}</span>
                                    </span>
                                    <DsIcon name='chevron-right' size={16} className='monitoringRow__chevron' />
                                </button>
                            )
                        }) : (
                          <NoAvailableData message="No monitoring alerts available." />)
                    }
                </Box> : (
                  <div className="barContainer">
                  {portfolioOptions?.series[0]?.data &&
                    portfolioOptions?.series[1]?.data &&
                    (portfolioOptions.series[0].data.some(value => value > 0) ||
                      portfolioOptions.series[1].data.some(value => value > 0)) ? (
                    <div className="barChart">
                      <Chart
                        options={portfolioOptions.options}
                        series={portfolioOptions.series}
                        type='bar'
                      />
                    </div>
                  ) : (
                    <NoAvailableData />
  
                  )}                    
                  <BarChartLegend />
                </div>
                )
              }
            </Card.Body>
          </Card>
        </Col>
        <Col className="mb-3 pl-3 pr-3" xl={6}>
          <Card>
            <Card.Body className='chartHeight'>
              <div className="applicationTitle">
                <div>
                  <span className='cardEyebrow'>{t.BY_STATUS}</span>
                  <Card.Title className='cardTitle'>
                    {t.APPLICATIONS}
                  </Card.Title>
                </div>
                <Card.Title className='cardTitleAside'>
                  {t.TOTAL_VALUE.replace('{value}', `$${(applicationValueDonut.series || [])
                    .reduce((sum, v) => sum + (Number(v) || 0), 0)
                    .toLocaleString('en-US')}`)}
                </Card.Title>
              </div>
              {applicationDonut.series &&
                applicationValueDonut.series &&
                (applicationDonut.series.some(value => value > 0) ||
                  applicationValueDonut.series.some(value => value > 0)) ? (
                <div className="donutRow">
                  <div className="donutContainer">
                    <Chart options={applicationDonut.options} series={applicationDonut.series} type="donut" />
                  </div>
                  <DonutChartLegend/>
                </div>
              ) : (
                <NoAvailableData message={t.NO_APPLICATION_DATA} />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {
        !isPlatformAdmin && <FMCSAsearch
          show={showModal}
          onHide={() => setModal(false)}
          getFmcsaData={getFmcsaData}
          fmcsaData={fmcsaData}
          queryUrl={fmcsaParam}
          history={history}
          creditCosts={creditCosts}
        />
      }
    </div>
  );
}

export default Dashboard;
